import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Linkedin, Facebook } from 'lucide-react';

const Footer = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const footerLinks = {
    Product: [
      { name: 'Features', path: '/features' },
      { name: 'Pricing', path: '/pricing' },
      { name: 'Templates', path: '/templates' },
      { name: 'Engine', path: '/products/engine' },
      { name: 'Exchange', path: '/products/exchange' },
      { name: 'Cadence', path: '/products/cadence' },
      { name: 'Updates', path: '/updates' }
    ],
    Company: [
      { name: 'About', path: '/about' },
      { name: 'Careers', path: '/careers' },
      { name: 'Contact', path: '/contact' },
      { name: 'Onboarding', path: '/onboarding' }
    ],
    Resources: [
      { name: 'Blog', path: '/blog' }
    ],
    Legal: [
      { name: 'Privacy Policy', path: '/privacy-policy' },
      { name: 'Terms & Conditions', path: '/terms-conditions' }
    ],
  };

  return (
    <footer className="bg-gray-900 text-gray-300 pt-16 pb-8 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 md:gap-12 mb-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <img
              src="https://customer-assets.emergentagent.com/job_f6991d0e-ab71-4b45-956d-bb71024e7667/artifacts/yzqu5gkr_Logo_Traain_white.png"
              alt="Traain Logo"
              className="h-12 w-auto mb-4"
            />
            <p className="text-sm text-gray-400 mb-6">
              Move teams forward with intelligent training and enablement.
            </p>
            <div className="flex space-x-4">
              <a
                href={process.env.REACT_APP_LINKEDIN_URL || "https://www.linkedin.com/company/traain"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href={process.env.REACT_APP_FACEBOOK_URL || "https://www.facebook.com/profile.php?id=61584378841435"}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-gray-700 transition-colors"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="font-semibold text-white mb-4">{category}</h3>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.name}>
                    <a
                      href={link.path}
                      className="text-sm hover:text-white transition-colors"
                      {...(link.path.startsWith('http') && {
                        target: '_blank',
                        rel: 'noopener noreferrer'
                      })}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter */}
        <div className="border-t border-gray-800 pt-8 mb-8">
          <div className="max-w-md">
            <h3 className="font-semibold text-white mb-2">Stay updated</h3>
            <p className="text-sm text-gray-400 mb-4">
              Get the latest news and updates from Traain.
            </p>
            <form className="flex gap-2" onSubmit={async (e) => {
              e.preventDefault();
              if (email && !isSubmitting) {
                setIsSubmitting(true);
                setMessage('');
                
                try {
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ email }),
                  });
                  
                  const data = await response.json();
                  
                  if (response.ok) {
                    setMessage(data.message || 'Successfully subscribed!');
                    setEmail('');
                    setTimeout(() => setMessage(''), 5000);
                  } else {
                    setMessage(data.detail || 'Failed to subscribe. Please try again.');
                  }
                } catch (error) {
                  setMessage('An error occurred. Please try again.');
                } finally {
                  setIsSubmitting(false);
                }
              }
            }}>
              <input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm focus:outline-none focus:border-blue-500 transition-colors disabled:opacity-50"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {message && (
              <p className={`text-sm mt-2 ${message.includes('success') || message.includes('subscribed') ? 'text-green-400' : 'text-red-400'}`}>
                {message}
              </p>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-center items-center">
          <p className="text-sm text-gray-400">© 2025 Traain. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;